import { defineStore } from "pinia";
import { ref, computed } from "vue";
import type { AdminOrganization } from "@/api/model";
import type { NetworkOrganizationWithCounts } from "@/api/model";
import { OrganizationTypeEnum } from "@/api/model/organizationTypeEnum";
import { useAuthStore } from "./auth";
import { adminV1OrganizationsRetrieve } from "@/api/admin-organizations/admin-organizations";
import { captureException } from "@sentry/vue";

export const useImpersonationStore = defineStore("impersonation", () => {
  // State
  const isImpersonating = ref(false);
  const impersonatedOrg = ref<AdminOrganization | null>(null);
  const originalDefaultOrg = ref<AdminOrganization | null>(null);

  // Register this store globally for access from auth store
  // This avoids circular dependency issues
  const registerGlobalAccess = () => {
    (globalThis as any).__IMPERSONATION_STORE__ = {
      get isImpersonating() {
        return isImpersonating.value;
      },
      get impersonatedOrg() {
        return impersonatedOrg.value;
      },
    };
  };

  // Getters
  const canImpersonate = computed(() => {
    const authStore = useAuthStore();
    const isTfaEmployee = Boolean(authStore.user?.is_tfa_employee);
    const isNetworkAdmin = Boolean(
      authStore.user?.default_organization?.organization_type ===
        OrganizationTypeEnum.network
    );
    return isTfaEmployee || isNetworkAdmin;
  });

  const currentOrganization = computed(() => {
    const authStore = useAuthStore();
    return isImpersonating.value
      ? impersonatedOrg.value
      : authStore.user?.default_organization;
  });

  // Actions
  const launchIntoOrg = (
    organization: AdminOrganization | NetworkOrganizationWithCounts
  ) => {
    if (!canImpersonate.value) {
      return;
    }

    const authStore = useAuthStore();

    // Store original org if not already impersonating
    if (!isImpersonating.value) {
      originalDefaultOrg.value = authStore.user?.default_organization || null;
    }

    // Set impersonation
    isImpersonating.value = true;
    impersonatedOrg.value = organization;

    // Update global access
    registerGlobalAccess();
  };

  const exitImpersonation = () => {
    if (!isImpersonating.value) {
      return;
    }

    isImpersonating.value = false;
    impersonatedOrg.value = null;
    originalDefaultOrg.value = null;

    // Update global access
    registerGlobalAccess();
  };

  // Action to restore impersonation from URL with full org data
  const restoreImpersonationFromUrl = (
    organization: AdminOrganization | NetworkOrganizationWithCounts
  ) => {
    const authStore = useAuthStore();

    // Store original org
    originalDefaultOrg.value = authStore.user?.default_organization || null;

    // Set impersonation without updating URL (it's already set)
    isImpersonating.value = true;
    impersonatedOrg.value = organization;

    // Update global access
    registerGlobalAccess();
  };

  // Restore impersonation from URL by fetching organization data
  const restoreImpersonationFromUrlById = async (orgId: string) => {
    try {
      // Fetch the specific organization directly by ID
      const org = await adminV1OrganizationsRetrieve(Number(orgId));

      if (org) {
        restoreImpersonationFromUrl(org);
        return true;
      } else {
        return false;
      }
    } catch (error: any) {
      captureException(error, {
        extra: {
          orgId,
          context: "restoreImpersonationFromUrlById",
        },
      });
      return false;
    }
  };

  const updateImpersonatedOrganization = (
    organization: Partial<AdminOrganization | NetworkOrganizationWithCounts>
  ) => {
    if (!impersonatedOrg.value) {
      return;
    }

    impersonatedOrg.value = {
      ...impersonatedOrg.value,
      ...organization,
    };
  };

  // Initialize global access
  registerGlobalAccess();

  return {
    // State
    isImpersonating,
    impersonatedOrg,
    originalDefaultOrg,
    canImpersonate,
    currentOrganization,
    launchIntoOrg,
    exitImpersonation,
    restoreImpersonationFromUrl,
    restoreImpersonationFromUrlById,
    updateImpersonatedOrganization,
  };
});
